import { FontIcon, PersonaSize, PrimaryButton, Stack, useTheme } from '@fluentui/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import useUserInfo from '../../shared/hooks/useUserInfo';
import { InlineDiv, NoOverflowText, Separator } from '../../shared/styled-components';
import { IAgentReport } from '../../models/reports.internal';
import { nameof } from '../../utils/stringUtils';
import CardContainer from './CardContainer';
import ElapsedTimeSpan from './ElapsedTimeSpan';
import TheUltimateBarChart from './TheUltimateBarChart';
import ChangeStateModal from '../../containers/ChangeStateModal';
import WorkItemList from './AgentCard.WorkItemList';
import { ActivityDiv, ActivityText, StyledPersona, TaskMetrics } from './AgentCard.components';
import { ActivityDetails } from './AgentCard.helpers';

export const StyledCardContainer = styled(CardContainer) <{ containerBoundingRectangle: DOMRect | undefined; heightOverflow: number }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  position: absolute;
  width: ${p => p.containerBoundingRectangle?.width ?? 0}px;
  left: ${p => p.containerBoundingRectangle?.x ?? 0}px;
  top: ${p => (p.containerBoundingRectangle?.y ?? 0) - p.heightOverflow}px;

  @keyframes cardExtend {
    0% {
      max-height: ${p => p.containerBoundingRectangle?.height ?? 0}px;
      overflow-y: hidden;
    }
    20% {
      max-height: ${p => p.containerBoundingRectangle?.height ?? 0}px;
      overflow-y: hidden;
    }
    100% {
      max-height: 100%;
      overflow-y: unset;
    }
  }

  animation: cardExtend 0.75s ease-in-out;
`;

const TopRow = styled.div`
  height: 86px; // from TaskMetrics
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;
`;

const InlineButton = styled(PrimaryButton)`
  min-width: unset;
  height: 21px;

  padding: 0.0625rem 0.375rem;
  border-radius: 1000px;
`;

function getHeightOverflow(containerBoundingRectangle: DOMRect | undefined, selfScrollHeight: number | undefined): number {
  if (!containerBoundingRectangle || !selfScrollHeight)
    return 0;

  const endY = containerBoundingRectangle.y + selfScrollHeight + 8;
  const overflow = Math.max(0, endY - window.innerHeight);

  return overflow;
}

interface IProps {
  agent: IAgentReport;
  activity: ActivityDetails;
  containerBoundingRectangle: DOMRect | undefined;
}

const ExpandedAgentCard: FC<IProps> = ({ agent, activity, containerBoundingRectangle }) => {
  const { palette } = useTheme();
  const { formatTime, formatTimeSpan } = useTimeFormatters();
  const { type } = useUserInfo();

  const totalTimeLoggedIn = useMemo(
    () => agent.timeSpentInStates.Busy + agent.timeSpentInStates.Break + agent.timeSpentInStates.Idle || 1,
    [agent.timeSpentInStates.Break, agent.timeSpentInStates.Busy, agent.timeSpentInStates.Idle],
  );

  const [isChangeStateOpen, setIsChangeStateOpen] = useState(false);

  const selfRef = useRef<HTMLDivElement>(null);
  const [heightOverflow, setHeightOverflow] = useState(0);

  useEffect(
    () => setHeightOverflow(getHeightOverflow(containerBoundingRectangle, selfRef.current?.scrollHeight)),
    [containerBoundingRectangle],
  );

  return <>
    <StyledCardContainer ref={selfRef} containerBoundingRectangle={containerBoundingRectangle} heightOverflow={heightOverflow}>
      <TopRow>
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', flex: '1 1 auto', minWidth: 1 } }}>
          <StyledPersona size={PersonaSize.size56}
            presence={activity.presence}
            text={agent.name}
            onRenderPrimaryText={() => (
              <InlineDiv gap="0.75rem" style={{ width: '100%' }}>
                <NoOverflowText>{agent.name}</NoOverflowText>
              </InlineDiv>
            )}
            onRenderSecondaryText={() => (
              <InlineDiv gap="0.75rem">
                {agent.currentState !== 'LoggedOut' && <InlineButton disabled={type !== 'supervisor'} onClick={() => setIsChangeStateOpen(true)}>Change state</InlineButton>}
                <InlineDiv gap="0.25rem">
                  <FontIcon iconName="Signin" style={{ fontSize: 16 }} />
                  <span>{agent.loggedInTimestamp ? formatTime(agent.loggedInTimestamp) : ''}</span>
                </InlineDiv>
                {
                  (agent.loggedOutTimestamp && agent.currentState === 'LoggedOut') &&
                  <InlineDiv gap="0.25rem">
                    <FontIcon iconName="SignOut" style={{ fontSize: 16 }} />
                    <span>{formatTime(agent.loggedOutTimestamp)}</span>
                  </InlineDiv>
                }
              </InlineDiv>
            )}
          />
          <ActivityDiv>
            {activity.icon && <FontIcon iconName={activity.icon} />}
            <ActivityText textStyle={activity.textStyle}>{activity.text}</ActivityText>
            <span>(<ElapsedTimeSpan startTimestamp={agent.currentStateSince} />)</span>
          </ActivityDiv>
        </Stack>
        <TaskMetrics agent={agent} />
      </TopRow>
      <div>
        <Separator>Agent Utilization</Separator>
        <TheUltimateBarChart
          values={[
            { color: palette.red, text: 'Busy', value: agent.timeSpentInStates.Busy },
            { color: palette.yellow, text: 'Break', value: agent.timeSpentInStates.Break },
            { color: palette.green, text: 'Available', value: agent.timeSpentInStates.Idle },
          ]}
          valueFormatter={(val) => `${Math.round(val / totalTimeLoggedIn * 100)}% (${formatTimeSpan(val)})`}
        />
      </div>
      {
        agent.currentlyHandledWorkItems.length > 0 &&
        <div>
          <Separator>Current Tasks</Separator>
          <WorkItemList workItems={agent.currentlyHandledWorkItems} />
        </div>
      }
    </StyledCardContainer>
    <ChangeStateModal
      isOpen={isChangeStateOpen}
      agent={agent}
      onSubmit={() => setIsChangeStateOpen(false)}
      onDismiss={() => setIsChangeStateOpen(false)}
    />
  </>;
};

ExpandedAgentCard.displayName = nameof({ ExpandedAgentCard });
export default ExpandedAgentCard;